import { Injectable } from '@angular/core';
import { TimezoneConversionService } from './timezone-conversion.service';

/**
 * Result of a datetime validation operation.
 */
export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Service responsible for validating datetime values.
 * Follows Single Responsibility Principle by focusing solely on validation logic.
 */
@Injectable({
  providedIn: 'root'
})
export class DateTimeValidationService {
  constructor(private timezoneConversionService: TimezoneConversionService) {}

  /**
   * Validates if a datetime in a specific timezone is in the past when converted to UTC.
   * 
   * @param date - The date to validate
   * @param timezone - The timezone string (e.g., "America/New_York (GMT-05:00)")
   * @returns ValidationResult indicating if the date is valid (not in the past)
   */
  isDateTimeInPast(date: Date, timezone: string): ValidationResult {
    if (!date) {
      return {
        isValid: false,
        errorMessage: 'Date is required'
      };
    }

    if (!timezone) {
      return {
        isValid: false,
        errorMessage: 'Timezone is required'
      };
    }

    try {
      const ianaTimezone = this.timezoneConversionService.extractIanaTimezone(timezone);
      const utcDate = this.timezoneConversionService.convertToUTC(date, ianaTimezone);
      const nowUTC = new Date();

      const isPast = utcDate < nowUTC;

      return {
        isValid: !isPast,
        errorMessage: isPast 
          ? `The selected datetime with ${timezone} is a past datetime. Please select a future datetime.`
          : undefined
      };
    } catch (error) {
      console.error('Error validating datetime:', error);
      return {
        isValid: false,
        errorMessage: 'An error occurred while validating the datetime'
      };
    }
  }

  /**
   * Validates a one-time schedule datetime.
   * 
   * @param date - The date to validate
   * @param timezone - The timezone string
   * @returns ValidationResult with validation status
   */
  validateOneTimeSchedule(date: Date, timezone: string): ValidationResult {
    return this.isDateTimeInPast(date, timezone);
  }
}

